<template>
  <div class="shifts-calendar-setting">
    <PageTitle
      :title="$t('reservationCalendar.title')"
      :btn="$t('common.button.create.text')"
      :btn2="$t('reservationCalendar.button.displaySetting.text')"
      btnFeature="admin.appointmentOrder.create"
      @btnClick="$router.push({ name: 'NewReservationCreate' })"
      @btn2Click="displaySettingDialog = true"
    />

    <FiltersContainer v-loading="loading">
      <ServiceUnitSearch
        :model.sync="search.unit"
        testName="search_unit"
        :styling="checkAction('admin.resourceItem.page') ? 'full-input': ''"
        @change="refresh"
      />
      <ServiceSearch
        :model.sync="search.service"
        testName="search_service"
        @change="refresh"
      />
      <BaseElSelect
        v-model="search.status"
        testName="search_status"
        :placeholder="$t('reservationCalendar.search.status.placeholder')"
        clearable
        @change="showAllSchedule()"
      >
        <BaseElSelectOption
          v-for="(item, index) in ['cancel', 'complete', 'wait', 'absence', 'confirm']"
          :key="`status-${index}`"
          :label="statusContent(item)"
          :value="item"
        />
      </BaseElSelect>
      <BaseElInput
        v-model="search.code"
        testName="search_code"
        clearable
        :placeholder="$t('reservationCalendar.search.code.placeholder')"
        @keypress.enter.native="refresh"
        @clear="refresh"
      >
        <i slot="prefix" class="el-input__icon el-icon-search" @click="refresh" />
      </BaseElInput>
      <ResourceSearch
        v-if="checkAction('admin.resourceItem.page')"
        testName="search_resource"
        class="flex-grow-1"
        :model.sync="search.resource"
        @change="refresh"
      />
      <BaseSearch
        v-if="checkAction('admin.resourceItemCategory.page')"
        testName="search_resourceCategory"
        class="flex-grow-1"
        :value.sync="search.resourceCategory"
        :api="GetResourceCategory"
        :placeholder="$t('reservationList.search.resourceCategory.placeholder')"
        multiple
        @change="refresh"
      />
      <BaseElInput
        v-model="search.search"
        :placeholder="$t('reservationList.search.userNameOrPhone.placeholder')"
        testName="search_search"
        class="test2"
        clearable
        @keypress.enter.native="refresh(true)"
        @clear="refresh(true)"
      >
        <i slot="prefix" class="el-input__icon el-icon-search" @click="refresh(true)" />
      </BaseElInput>
    </FiltersContainer>

    <div class="display-content-hint">
      <p>{{ $t('reservationCalendar.diplayContent.hint.title') }}<span v-if="!loading">{{ displayContentHint() }}</span></p>
      <p>★：預約訂單含有會員備註或店家備註</p>
    </div>

    <section v-loading="loading" class="calendar-container">
      <FullCalendar v-if="show" ref="calendar" :options="showCalendar">
        <template v-slot:eventContent="arg">
          <div
            class="flex items-start overflow-x-auto p-[4px]"
            :class="{
              'event-disabled': arg.event.extendedProps.disabled,
            }"
            style="gap: 4.5px"
          >
            <div class="flex items-center" style="gap: 4px">
              <div
                v-if="arg.event.extendedProps.hasComment"
                :style="arg.view.type === 'dayGridMonth' ? `color: ${arg.event.backgroundColor}` : ''"
              >
                ★
              </div>
              <div
                v-else
                class="event-color-dot"
                :style="`background: ${arg.view.type === 'dayGridMonth' ? arg.event.backgroundColor : 'white'}`"
              />
            </div>
            <p class="fc-event-title break-all">{{ arg.event.title }}</p>
          </div>
        </template>
      </FullCalendar>
    </section>

    <!-- 預約資訊側邊彈窗 -->
    <ReservationOrderDetail
      v-if="modifyDialog"
      :orderId="selectOrderId"
      :enableCancelReasonCollection="enableCancelReasonCollection"
      @close="modifyDialog=false"
      @cancel="onCancel"
      @refresh="refresh"
    />

    <DeleteDialog
      v-if="deleteDialog"
      title="警示"
      :content="deleteContent"
      width="40%"
      cancelBtnString="返回"
      @close="deleteDialog = false"
      @delete="cancelReservation(), deleteDialog=false"
    />

    <ReservationCalendarDisplaySettingModal
      v-if="displaySettingDialog"
      @close="displaySettingDialog = false"
      @refresh="refresh"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import ReservationOrderDetail from '@/components/Reservation/ReservationOrderDetail/ReservationOrderDetail.vue'
import {
  GetReservationSimple,
  GetReservationCount,
  FindReservation,
  UpdateReservationOrderStatus,
} from '@/api/reservation'
import DeleteDialog from '@/components/Dialog/DeleteDialog.vue'
import { FindMember } from '@/api/member'
import { statusTranslate } from '@/utils/helper'
import MixinFunc from '@/components/MixinFunc'
import ServiceUnitSearch from '@/components/Search/ServiceUnitSearch.vue'
import ServiceSearch from '@/components/Search/ServiceSearch.vue'
import ResourceSearch from '@/components/Search/ResourceSearch.vue'
import BaseSearch from '@/components/Search/BaseSearch.vue'
import ReservationCalendarDisplaySettingModal from './components/ReservationCalendarDisplaySettingModal.vue'
import dayjs from '@/lib/dayjs'
import { get, join, isEmpty, map, filter } from 'lodash'
import { useDisplaySettings } from '@/use/displaySettings'
import { useReservation } from '@/use/useReservation'
import { displayContentHintConfig, displayColorHintConfig, calendarConfigs } from '@/config/reservationCalendar'
import { usePermissions } from '@/use/permissions'
import { i18n } from '@/plugins/i18n/i18n'
import { useFetch } from '@/use/fetch'
import { computed, defineComponent, ref } from 'vue'
import { GetResourceCategory } from '@/api/resourceCategory'
import { formatDate } from '@/utils/date'
import { useShop } from '@/use/shop'

export default defineComponent({
  name: 'ReservationCalendar',
  components: {
    FullCalendar,
    DeleteDialog,
    ServiceUnitSearch,
    ResourceSearch,
    ServiceSearch,
    ReservationOrderDetail,
    ReservationCalendarDisplaySettingModal,
    BaseSearch,
  },
  mixins: [MixinFunc],
  setup (props) {
    const { shopTimezone } = useShop()
    const { checkAction } = usePermissions()
    const { fetchAll } = useFetch()
    const { getDisplaySettings, displaySettings } = useDisplaySettings('reservationCalendar', {
      displayContent: 'serviceUnit-service',
      displayColor: 'order-status',
    })
    const { configData, getConfig } = useReservation()
    const calendarEvents = ref([])
    const enableCancelReasonCollection = computed(() => get(configData.reservation, 'enableCancelReasonCollection', false))
    const displayContentHint = () => {
      const showLabels = []
      const displayContent = get(displaySettings.value, 'displayContent')
      const displayColor = get(displaySettings.value, 'displayColor')
      if (!displayContent) showLabels.push(i18n.t(get(displayContentHintConfig, 'serviceUnit-service.label')))
      if (displayContent) showLabels.push(i18n.t(get(displayContentHintConfig, `${displayContent}.label`)))
      if (!displayColor) showLabels.push(i18n.t(get(displayColorHintConfig, 'order-status.label')))
      if (displayColor) showLabels.push(i18n.t(get(displayColorHintConfig, `${displayColor}.label`)))
      return showLabels.join('、')
    }

    const useOrderStatusColor = (status) => {
      const colors = {
        cancel: '#F2697A',
        wait: '#E4A65F',
        complete: '#B5C93E',
      }
      return get(colors, status)
    }

    const setDataTestid = () => {
      const nextMonthBtn = get(Array.from(document.getElementsByClassName('fc-next-button')), [0])
      const preMonthBtn = get(Array.from(document.getElementsByClassName('fc-prev-button')), [0])
      const calendarTitle = get(Array.from(document.getElementsByClassName('fc-toolbar-title')), [0])

      if (nextMonthBtn) {
        nextMonthBtn.setAttribute('data-testid', 'next-month-btn')
      }
      if (preMonthBtn) {
        preMonthBtn.setAttribute('data-testid', 'prev-month-btn')
      }
      if (calendarTitle) {
        calendarTitle.setAttribute('data-testid', 'calendar-title')
      }
    }

    return {
      fetchAll,
      getDisplaySettings,
      displaySettings,
      displayContentHint,
      useOrderStatusColor,
      checkAction,
      setDataTestid,
      calendarEvents,
      GetResourceCategory,
      shopTimezone,
      getConfig,
      enableCancelReasonCollection,
    }
  },

  data () {
    return {
      deleteContent: '',
      show: true,
      loading: false,
      modifyDialog: false,
      createDialog: false,
      deleteDialog: false,
      displaySettingDialog: false,
      showModify: false,
      showUserInfo: false,
      userInfo: null,
      modifyType: '',
      selectOrderId: null,

      reservationsCount: 0,
      reservationsList: [],

      selecReservation: {
        AppointmentService: {},
        AppointmentUnit: {},
        AppointmentServiceAttaches: [],
      },

      // 當前月曆顯示的日期區間
      calendarRange: {
        start: null,
        end: null,
      },
      search: {
        date: null,
        search: '',
        unit: '',
        service: '',
        status: '',
        code: '',
        resource: '',
        resourceCategory: '',
      },
    }
  },

  computed: {
    ...mapGetters([
      'shop',
    ]),
    showCalendar () {
      return {
        plugins: [dayGridPlugin, interactionPlugin, timeGridPlugin],
        eventDidMount: (e) => {
          if (e.el) e.el.setAttribute('data-testid', `calendar-event-${e.event?.title}`)
        },
        headerToolbar: {
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay',
        },
        // buttonText: {
        //   month: '月檢視',
        //   week: '週檢視',
        //   day: '天檢視',
        //   list: '列表',
        // },
        initialView: 'dayGridMonth',
        weekends: true,
        height: '100%',
        selectable: true,
        displayEventTime: false,
        displayEventEnd: true,
        eventTimeFormat: {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        },
        dateClick: this.showCalendarDateClick,
        eventClick: this.modifyReservation,
        events: this.calendarEvents,
        locale: this.calendarContext, // Use the current language context
        dayMaxEventRows: true, // for all non-TimeGrid views
        views: {
          timeGrid: {
            // dayMaxEventRows: 6 // adjust to 6 only for timeGridWeek/timeGridDay
          },
          dayGrid: {
            dayMaxEventRows: 10, // adjust to 6 only for timeGridWeek/timeGridDay
          },
        },
        // 處理月曆 Toolbar 按鈕操控事件
        customButtons: {
          prev: {
            click: () => {
              this.$refs.calendar.getApi().prev()
              this.changeCalendar()
            },
          },
          next: {
            click: () => {
              this.$refs.calendar.getApi().next()
              this.changeCalendar()
            },
          },
          today: {
            text: this.calendarContext.buttonText.today,
            click: () => {
              this.$refs.calendar.getApi().today()
              this.changeCalendar()
            },
          },
          timeGridWeek: {
            text: this.calendarContext.buttonText.week,
            click: () => {
              this.$refs.calendar.getApi().changeView('timeGridWeek')
              this.changeCalendar()
            },
          },
        },
        timeZone: this.shopTimezone,
      }
    },
    calendarContext () {
      return calendarConfigs[i18n.locale] || calendarConfigs.default
    },
    activeStart () {
      return this.$refs.calendar.getApi().view.activeStart
    },
    activeEnd () {
      return this.$refs.calendar.getApi().view.activeEnd
    },

    attachServices () {
      if (this.selecReservation.AppointmentServiceAttaches.length === 0) return [{ name: '無', id: 0 }]
      return this.selecReservation.AppointmentServiceAttaches
    },

    memberTags () {
      const mTags = this.userInfo.MTags
      const sTags = this.userInfo.SystemMTag
      if (mTags.length === 0 && this.userInfo.SystemMTag.length === 0) return [{ name: '無', id: 0 }]
      const list = []
      mTags.forEach(item => {
        list.push(item)
      })
      sTags.forEach(item => {
        list.push(item)
      })
      return list
    },

    // 處理搜尋排班班表區間資料
    calendarRangeString () {
      const format = 'YYYY-MM-DD'
      const { start, end } = this.calendarRange
      let dayStart, dayEnd
      if (start && end) {
        dayStart = start.format(format)
        dayEnd = end.format(format)
      } else {
        dayStart = dayjs().startOf('month').format(format)
        dayEnd = dayjs().endOf('month').format(format)
      }
      return { dayStart, dayEnd }
    },

    sidebar () { return this.$store.state.app.sidebar },
  },

  watch: {
    sidebar () {
      // const api = this.$refs.calendar.getApi()
      // api.render()
      this.show = false
      setTimeout(() => {
        this.show = true
      }, 300)
      console.log('resize')
    },
  },

  async mounted () {
    this.calendarRange = this.getCalendarDate()
    this.setDataTestid()
    await this.refresh()

    window.gotoDate = (date) => {
      const api = this.$refs.calendar.getApi()
      api.gotoDate(date)
      this.changeCalendar()
    }

    // 取得預約設定（取消原因收集開關）；擺最後避免設定取得失敗時擋住行事曆載入
    await this.getConfig('reservation')
  },
  beforeDestroy () {
    delete window.gotoDate
  },

  methods: {
    searchDate (data) {
      this.$refs.calendar.getApi().gotoDate(data)
    },
    statusContent: (status) => statusTranslate(status),
    onCancel (data, cancelReason) {
      this.selecReservation = data
      // 取消原因由抽屜內部的彈窗收集後一併帶回，直接送出
      if (cancelReason) {
        this.cancelReservation(cancelReason)
        return
      }
      const orders = get(data, 'AppointmentOrder.AppointmentReservations')
      const orderCode = get(data, 'AppointmentOrder.code')
      // const numberCodes = map(orders, 'numberCode')
      // const totalOrde = max(numberCodes)
      // this.deleteDialog = true
      if (!orders) {
        this.deleteDialog = true
        this.deleteContent = '確定要取消此筆預約？'
        return
      }
      if (!orders.length || orders.length === 1) {
        this.deleteDialog = true
        this.deleteContent = '確定要取消此筆預約？'
        return
      }

      const ordersCount = orders.length

      this.deleteContent = `訂單編號 ${orderCode} 有 ${ordersCount} 筆預約紀錄，將同時取消 ${ordersCount} 筆預約紀錄。`
      this.deleteDialog = true
    },
    //= > 取得月曆日期
    getCalendarDate () {
      const calendarApi = this.$refs.calendar.getApi()
      calendarApi.getDate()
      const date = dayjs(calendarApi.getDate())
      return {
        start: date.startOf('month').startOf('week'),
        end: date.endOf('month').endOf('week'),
      }
    },

    //= > 切換月曆(前後)
    async changeCalendar () {
      const prevDayStart = this.calendarRangeString.dayStart
      this.calendarRange = this.getCalendarDate()
      const currentDayStart = this.calendarRangeString.dayStart
      if (prevDayStart === currentDayStart) return
      await this.refresh()
    },

    async refresh () {
      this.getDisplaySettings()
      await this.getReservation()
      await this.getReservationCount()
      await this.showAllSchedule()
    },

    modify (type) {
      this.showModify = true
      this.modifyType = type
    },

    async modifyReservation (e) {
      this.modifyDialog = true
      this.selectOrderId = e.event.extendedProps.id
      // const res = await this.findReservation(e.event.extendedProps.id)
      // this.selecReservation = res
      // await this.findMember()
    },

    //= > 取得預約
    async getReservation () {
      this.loading = true
      this.reservationsList = []
      const payload = {
        shopId: this.shop,
        start: 0,
        dateStart: this.search.date ? dayjs(this.search.date).toDate() : dayjs(this.calendarRangeString.dayStart).subtract(7, 'day').toDate(), // 七天前
        dateEnd: this.search.date ? dayjs(this.search.date).add(1, 'day').toDate() : dayjs(this.calendarRangeString.dayEnd).add(8, 'day').toDate(), // 七天後
        search: (this.search.search === '') ? undefined : this.search.search.trim(),
        AppointmentUnitId: (this.search.unit === '') ? undefined : this.search.unit.id,
        AppointmentServiceId: (this.search.service === '') ? undefined : this.search.service.id,
        ResourceItemId: (this.search.resource === '') ? undefined : this.search.resource.id,
        resourceItemCategoryIds: isEmpty(this.search.resourceCategory) ? undefined : join(map(this.search.resourceCategory, 'id'), ','),
        code: (this.search.code === '') ? undefined : this.search.code,
      }

      await this.fetchAll(GetReservationSimple, payload, (res) => {
        this.loading = false
        this.reservationsList = res
      }, (err) => {
        this.loading = false
        this.$message.error({
          message: err || err.message,
        })
      }, { limit: 500 })
    },

    //= > 取得預約總數
    async getReservationCount () {
      try {
        this.reservationsCount = await GetReservationCount({
          shopId: this.shop,
          dateStart: (this.search.date === null) ? dayjs(this.calendarRangeString.dayStart).subtract(7, 'day').toDate() : dayjs(this.search.date).toDate(),
          dateEnd: (this.search.date === null) ? dayjs(this.calendarRangeString.dayEnd).add(8, 'day').toDate() : dayjs(this.search.date).add(1, 'day').toDate(),
          search: (this.search.search === '') ? undefined : this.search.search.trim(),
          AppointmentUnitId: (this.search.unit === '') ? undefined : this.search.unit.id,
          AppointmentServiceId: (this.search.service === '') ? undefined : this.search.service.id,
          ResourceItemId: (this.search.resource === '') ? undefined : this.search.resource.id,
          resourceItemCategoryIds: isEmpty(this.search.resourceCategory) ? undefined : join(map(this.search.resourceCategory, 'id'), ','),
          code: (this.search.code === '') ? undefined : this.search.code,
        })
      } catch (error) {
        this.$message.error({
          message: error || error.message,
        })
      }
    },

    async findReservation (id) {
      const res = await FindReservation({
        shopId: this.shop,
        id,
      })
      return res
    },

    async findMember () {
      if (this.selecReservation.Member === null) return
      const res = await FindMember({
        shopId: this.shop,
        id: this.selecReservation.Member.id,
      })
      this.userInfo = res
    },

    showAllSchedule () {
      this.showCalendar.events = []
      this.calendarEvents = []
      const onFilterStatus = this.search.status !== ''
      // const onFilterUnit = this.search.unit !== ''
      let reservationsList = this.reservationsList
      if (onFilterStatus) {
        reservationsList = filter(reservationsList, (item) => get(item, 'status') === this.search.status)
      }
      reservationsList.forEach(item => {
        const status = get(item, 'status')
        let color = ''

        // 顯示顏色
        const displayColor = get(this.displaySettings, 'displayColor')
        if (displayColor === 'order-status') color = this.useOrderStatusColor(status)
        if (displayColor === 'service-unit') color = get(item, 'unitColor', 'gray')

        // 時段
        // const timeStart = formatDate(item.start) // dayjs(item.start).format('YYYY-MM-DD HH:mm')
        // const timeStart = dayjs(item.start).format('YYYY-MM-DD HH:mm')
        // const timeEnd = dayjs(item.end).format('YYYY-MM-DD HH:mm')
        const timeStart = formatDate(item.start, 'YYYY-MM-DD HH:mm')
        const timeEnd = formatDate(item.end, 'YYYY-MM-DD HH:mm')

        const isEndSameDay = dayjs(timeEnd).isSame(dayjs(timeStart), 'date')
        const crossDate = dayjs(item.start).add(1, 'day').get('date')

        // 顯示資訊內容
        const displayContent = get(this.displaySettings, 'displayContent')

        const formatedDate = dayjs(timeStart).format('YYYY-MM-DD')
        let title = `${item.unitName} ${item.serviceName}`
        let className = `${formatedDate}-${item.unitName}-${item.serviceName}`
        const visitorTitle = item.origin === 'pubApt' ? '(訪客)' : ''
        const contentMap = {
          'serviceUnit-service': `${visitorTitle} ${item.unitName || '-'} ${item.serviceName}`,
          'member-service': `${visitorTitle} ${item.userName || '-'} ${item.serviceName}`,
          'resource-service': `${visitorTitle} ${item.resourceName || '-'} ${item.serviceName}`,
          'serviceUnit-member': `${visitorTitle} ${item.unitName || '-'} ${get(item, 'userName', '訪客')}`,
        }

        title = get(contentMap, displayContent) || title
        className = `${formatedDate}-${visitorTitle}-${contentMap[displayContent]?.replace(/\s+/g, '-') || ''}`

        const startPeriod = dayjs(timeStart).format('HH:mm')
        const endPeriod = dayjs(timeEnd).format('HH:mm')
        let displayTitle = `${startPeriod} - ${endPeriod} ${title}`
        if (!isEndSameDay) displayTitle = `${startPeriod} - ${crossDate}日 ${endPeriod} ${title}`

        this.createCalendarEvent({
          title: displayTitle,
          timeStart,
          timeEnd: isEndSameDay ? timeEnd : dayjs(timeEnd).subtract(24, 'h').format('YYYY-MM-DD HH:mm'),
          allDay: false,
          props: item,
          color,
          className,
        })
      })
    },

    //= > 創建月曆事件
    createCalendarEvent ({ title, timeStart, timeEnd, props, color, className }) {
      const data = {
        title,
        start: timeStart,
        end: timeEnd,
        allDay: false,
        display: 'list-item',
        extendedProps: props,
        backgroundColor: color,
        className,
      }

      this.calendarEvents.push(data)
    },

    //= > 刪除(取消)預約
    async cancelReservation ({ cancelReasonCode, cancelReasonNote } = {}) {
      const [, err] = await UpdateReservationOrderStatus({
        shopId: this.shop,
        id: get(this.selecReservation, 'AppointmentOrder.id'),
        status: 'cancel',
        cancelReasonCode,
        cancelReasonNote,
      })

      if (err) return this.$message.error(err)
      this.$message.warning(i18n.t('reservationCalendar.message.chancelReservationSuccess'))
      this.modifyDialog = false
      this.refresh()
    },
  },
})
</script>

<style scoped lang="postcss">
header {
  margin-right: 3rem;
}

section {
  flex: 1;
}

.calendar-container {
  height: 100vh;
}

.display-content-hint {
  @apply text-primary-100 font-medium mb-[20px];
}

.event-color-dot {
  @apply w-[8.5px] h-[8.5px] rounded-full flex-shrink-0;
  @apply relative top-[5px];
}
</style>
